import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const carouselImages = [
  { src: '/photos/IMG_2335.jpg', position: '70% 55%' },
  { src: '/photos/IMG_0520.jpeg', position: 'center 10%' },
  { src: '/photos/IMG_proposal.JPG', position: 'center 5%' },
];

export const HomePage = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % carouselImages.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative" data-testid="home-page">
      {/* Full-width Photo Carousel with Overlaid Names */}
      <div className="relative w-full h-[85vh] sm:h-[90vh] overflow-hidden">
        {/* Stack all images; only the current one is visible */}
        {carouselImages.map((image, index) => (
          <img
            key={index}
            src={image.src}
            alt="Adrienne and Thomas"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1.5s] ease-in-out"
            style={{ opacity: index === currentImage ? 1 : 0, objectPosition: image.position }}
          />
        ))}

        {/* Overlay gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/15" />

        {/* Names and Details overlaid on image */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 sm:pb-20 px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-5xl sm:text-6xl md:text-7xl font-normal text-white mb-4 drop-shadow-lg"
            data-testid="couple-names"
          >
            Adrienne & Thomas
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-white/90 text-sm uppercase tracking-[0.25em] font-medium mb-3"
          >
            {t('weddingCelebration')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="w-12 h-0.5 bg-white/70 mx-auto mb-4"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="font-serif text-xl sm:text-2xl text-white/90 mb-1 drop-shadow"
            data-testid="wedding-date"
          >
            September 5th, 2026
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="font-serif text-white/75 text-lg sm:text-xl drop-shadow"
            data-testid="wedding-venue"
          >
            Château des Joncherets, France
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8"
          >
            <a
              href="#rsvp"
              className="inline-block text-sm px-6 py-2.5 rounded bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 transition-colors"
            >
              {t('rsvp')}
            </a>
          </motion.div>
        </div>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentImage ? 'bg-white scale-110' : 'bg-white/50'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
